import { AuthProvider as AuthUiProvider, useAuthMe } from '@pulse/auth-ui';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { getAdminEnvironment } from '@/env';

/**
 * Wraps the auth-ui provider with the admin app's redirect-to-login behavior.
 *
 * The actual token storage, API client, and identity caching live in
 * `@pulse/auth-ui` so the same wiring is reusable across other agency SPAs.
 * This file only adds the bits that are admin-specific (where to send the
 * user when their session ends).
 */
export function AuthProvider({ children }: { readonly children: React.ReactNode }): React.ReactElement {
  const env = React.useMemo(() => getAdminEnvironment(), []);
  // The API mounts the public auth router and admin user-management router under `/api`, so
  // apiBase must include the prefix. Otherwise the auth-ui client (which calls `/auth/login`,
  // `/admin/users`, etc.) would hit `localhost:5300/auth/login` and 404. Same gotcha that
  // platform-console handles for its own `/platform` prefix — see its AuthGuard.tsx.
  const apiBase = React.useMemo(() => `${env.apiUrl.replace(/\/$/, '')}/api`, [env.apiUrl]);
  return (
    <AuthUiProvider apiBase={apiBase}>
      <UnauthenticatedRedirector />
      {children}
    </AuthUiProvider>
  );
}

/**
 * Tiny no-render component that listens for transitions to "no user" and
 * pushes the browser to `/login`. Sits inside the provider so it can read the
 * shared identity state without forcing every consumer to wire its own
 * subscription.
 */
function UnauthenticatedRedirector(): null {
  const navigate = useNavigate();
  const { user, loading } = useAuthMe();

  React.useEffect(() => {
    if (loading) return;
    if (user !== null) return;
    if (typeof window === 'undefined') return;
    // Local development on the API's dev-bypass session has no token: ProtectedRoute decides.
    if (getAdminEnvironment().devInsecureSession) return;
    const path = window.location.pathname;
    // Don't bounce the user away from auth pages they're already on. The `/auth/*` group
    // covers the social-callback handoff (`/auth/callback`) and the link-credentials page
    // (`/auth/link-account`) — both run an exchange before a session token exists, so
    // bouncing them to /login here would race the exchange and strand the user with
    // `?next=/auth/callback?code=…` (and a stale, single-use exchange code).
    if (
      path === '/login' ||
      path.startsWith('/signup') ||
      path.startsWith('/recovery') ||
      path.startsWith('/mfa') ||
      path.startsWith('/verify') ||
      path.startsWith('/auth/')
    ) {
      return;
    }
    const next = `${window.location.pathname}${window.location.search}`;
    navigate(`/login?next=${encodeURIComponent(next)}`, { replace: true });
  }, [user, loading, navigate]);

  return null;
}

/** Compatibility wrapper for callers that still import the legacy `useAuth`. */
export interface AuthenticatedSession {
  readonly userId: string;
  readonly email: string | undefined;
  readonly displayName: string | undefined;
  readonly tenantId: string | undefined;
  readonly groups: readonly string[];
}

export function useAuth(): {
  readonly status: 'loading' | 'authenticated' | 'unauthenticated';
  readonly session: AuthenticatedSession | null;
  readonly reload: () => Promise<void>;
} {
  const { user, loading, reload } = useAuthMe();
  const status: 'loading' | 'authenticated' | 'unauthenticated' = loading
    ? 'loading'
    : user === null
      ? 'unauthenticated'
      : 'authenticated';
  const session: AuthenticatedSession | null =
    user === null
      ? null
      : {
          userId: user.userId,
          email: user.email,
          displayName: user.displayName ?? undefined,
          tenantId: user.tenantId ?? undefined,
          groups: user.groups,
        };
  return { status, session, reload };
}
